<template>
  <div style="padding: 5px" class="user">
    <div class="menu">
      <header>
        <h2>{{ $t('user.title') }}</h2>
        <a-space>
          <a-button type="primary" @click="handleSearch" v-if="authorities.find(a => a.code === 'auth:user:query' || a.code === 'agent:auth:user:query')">{{ $t('buttons.search') }}</a-button>
          <a-button type="primary" @click="() => beforeOpenModal()" v-if="authorities.find(a => a.code === 'auth:user:insert' || a.code === 'auth:user:insert:customer' || a.code === 'agent:auth:user:insert')" :loading="insertLoading">{{ $t('buttons.add') }}</a-button>
          <a-button type="primary" @click="showBatchPutModal" v-if="authorities.find(a => a.code === 'auth:user:update' || a.code === 'auth:user:update:customer' || a.code === 'agent:auth:user:update')">{{ $t('user.bashPut') }}</a-button>
          <a-button type="primary" @click="activeAnalyse">{{ $t('user.active') }}</a-button>

        </a-space>
      </header>
      <a-table
        :columns="columns"
        :data-source="data"
        :row-key="record => record.id"
        :pagination="pagination"
        :loading="isLoading"
        :scroll="scroll"
        :row-selection="rowSelection"
        @change="handleTableChange"
      >
        <template v-slot:realName="text">
          <text-highlight :queries="[highLightInfo.realName]">{{text}}</text-highlight>
        </template>
        <template v-slot:username="text">
          <text-highlight :queries="[highLightInfo.username]">{{text}}</text-highlight>
        </template>
        <template v-slot:organization="text">
          <text-highlight :queries="[highLightInfo.organization]">{{text}}</text-highlight>
        </template>
        <template v-slot:role="text">
          <text-highlight :queries="[highLightInfo.role]">{{text}}</text-highlight>
        </template>
        <template v-slot:loginPlatform="text">
          {{ loginPlatform[text][lang] }}
        </template>
        <template v-slot:requiredCaptcha="text">
          {{ text ? $t('common.yes') : $t('common.no') }}
        </template>
        <template v-slot:icon="text">
          <a-icon :type="text" style="font-size: 20px" />
        </template>
        <template v-slot:enabled="text">
          {{ text ? $t('common.yes') : $t('common.no') }}
        </template>
        <template #operation="text, record">
          <a-button type="link" style="padding-left: 0" @click="showEditModal(record)" v-if="authorities.find(a => a.code === 'auth:user:update' || a.code === 'auth:user:update:customer' || a.code === 'agent:auth:user:update')" :loading="record.loading">{{ $t('buttons.edit') }}</a-button>
          <a-popconfirm
            :key="lang"
            v-if="authorities.find(a => a.code === 'auth:user:delete' || a.code === 'agent:auth:user:delete')"
            :title="$t('messages.ctrd')"
            :ok-text="$t('common.okText')"
            :cancel-text="$t('common.cancelText')"
            @confirm="removeUser(record)"
          >
            <a href="#">{{ $t('buttons.delete') }}</a>
          </a-popconfirm>
        </template>
      </a-table>
      <UserModal :visible="visible" :authorities="authorities" :id="id" :closeModl="closeModal" :afterClose="handleAfterClose" :info="record" :mobile="mobile" />
    </div>
    <a-modal v-model="isPutModalVisible" :title="$t('user.bashPut')" :afterClose="clearAppVersionForm" :confirm-loading="isAppVersionUpdating" @ok="updateAppVersions">
      <a-form-model ref="appVersionForm" :model="appVersionForm" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
        <a-form-model-item :label="$t('user.newApp')" prop="appVersion">
          <a-input v-model="appVersionForm.appVersion" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import moment from 'moment'
import TreeNodes from '@/components/common/TreeNodes.vue'
import AnalysisPage from './components/AnalysisPage.vue'

import {
  searchOrganizationList
} from '@/api/auth/organization'
import {
  searchRoleList
} from '@/api/auth/role'

import {
  searchUserList,
  addUser,
  updateUser,
  removeUser,
  updateAppVersions
} from '@/api/auth/user'
import TextHighlight from 'vue-text-highlight'
import UserModal from '@/components/common/UserModal.vue'
import * as echarts from 'echarts';
import { SearchOutlined } from '@ant-design/icons-vue';

const booleanMap = {
  '是': true,
  'yes': true,
  '否': false,
  'no': false,
  '全部': 0,
  all: 0,
  '移动端': 1,
  'mobile': 1,
  'PC端': 2,
  'PC': 2
}

export default {
  components: { TextHighlight, TreeNodes, UserModal,AnalysisPage },
  data() {
    return {
      data: [],
      labelCol: { span: 4 },
      authorities: [],
      wrapperCol: { span: 16 },
      visible: false,
      isLoading: false,
      mobile: '',
      roles: [],
      highLightInfo: {
        realName: '',
        username: '',
        organization: '',
        role: ''
      },
      loginPlatform: [
        {
          zh: '全部',
          en: 'all'
        },
        {
          zh: '移动端',
          en: 'mobile'
        },
        {
          zh: 'PC端',
          en: 'PC'
        }
      ],
      columns: [
        {
          title: () => (<div>{ this.$t('user.columns.name') }<span class="icons unset" style="cursor: pointer;" data-index="0"><a-icon type="caret-up" /><a-icon type="caret-down" /></span><a-input allow-clear class="search" type="text" data-index="0" /></div>),
          dataIndex: 'realName',
          key: 'realName',
          scopedSlots: {
            customRender: 'realName'
          }
        },
        {
          title: () => (<div>{ this.$t('user.columns.mobile') }<span class="icons unset" style="cursor: pointer;" data-index="1"><a-icon type="caret-up" /><a-icon type="caret-down" /></span><a-input allow-clear class="search" type="text" data-index="1" /></div>),
          dataIndex: 'username',
          key: 'username',
          scopedSlots: {
            customRender: 'username'
          }
        },
        {
          title: () => (<div>{ this.$t('user.columns.organization') }<span class="icons unset" style="cursor: pointer;" data-index="2"><a-icon type="caret-up" /><a-icon type="caret-down" /></span><a-input allow-clear class="search" type="text" data-index="2" /></div>),
          dataIndex: 'organization',
          key: 'organization',
          scopedSlots: {
            customRender: 'organization'
          }
        },
        {
          title: () => (<div>{ this.$t('user.columns.role') }<span class="icons unset" style="cursor: pointer;" data-index="3"><a-icon type="caret-up" /><a-icon type="caret-down" /></span><a-input allow-clear class="search" type="text" data-index="3" /></div>),
          dataIndex: 'role',
          key: 'role',
          scopedSlots: {
            customRender: 'role'
          }
        },
        {
          title: () => (<div>{ this.$t('user.columns.requiredCaptcha') }<a-select data-index="4" style="width: 130px"><a-select-option value="none">{ this.$t('common.all') }</a-select-option><a-select-option value="true">{ this.$t('common.yes') }</a-select-option><a-select-option value="false">{ this.$t('common.no') }</a-select-option></a-select></div>),
          dataIndex: 'requiredCaptcha',
          key: 'requiredCaptcha',
          width: 170,
          scopedSlots: {
            customRender: 'requiredCaptcha'
          }
        },
        {
          title: () => (<div>{ this.$t('user.columns.enabled') }<br/><a-select data-index="5" style="width: 70px"><a-select-option value="none">{ this.$t('common.all') }</a-select-option><a-select-option value="true">{ this.$t('common.yes') }</a-select-option><a-select-option value="false">{ this.$t('common.no') }</a-select-option></a-select></div>),
          dataIndex: 'enabled',
          key: 'enabled',
          width: 120,
          scopedSlots: {
            customRender: 'enabled'
          }
        },
        {
          title: () => (<div>{ this.$t('user.columns.loginPlatform') }<br/><a-select data-index="6" style="width: 80px"><a-select-option value="0">{ this.loginPlatform[0][this.lang] }</a-select-option><a-select-option value="1">{ this.loginPlatform[1][this.lang] }</a-select-option><a-select-option value="2">{ this.loginPlatform[2][this.lang] }</a-select-option></a-select></div>),
          dataIndex: 'loginPlatform',
          key: 'loginPlatform',
          width: 130,
          scopedSlots: {
            customRender: 'loginPlatform'
          }
        },
        {
          title: () => (<div>{ this.$t('common.createdAt') }<span class="icons unset" style="cursor: pointer; right: -52px" data-index="7"><a-icon type="caret-up" /><a-icon type="caret-down" /></span></div>),
          dataIndex: 'createdAt',
          key: 'createdAt',
          width: 165
        },
        {
          title: () => (<div>{ this.$t('common.updatedAt') }<span class="icons DESC" style="cursor: pointer; right: -52px" data-index="8"><a-icon type="caret-up" /><a-icon type="caret-down" /></span></div>),
          dataIndex: 'updatedAt',
          key: 'updatedAt',
          width: 165
        },
        {
          title: () => this.$t('common.operation'),
          key: 'operation',
          width: 130,
          align: 'center',
          scopedSlots: { customRender: 'operation' },
          fixed: 'right'
        }
      ],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0
      },
      pids: {
        0: '无'
      },
      sortInfo: [],
      searchInfo: {
        text: {
          protocolCode: '',
          protocolName: ''
        },
        column: {
          protocolCode: false,
          protocolName: false
        }
      },
      requiredCaptcha: '',
      enabled: '',
      organizationsPage: 1,
      rolePage: 1,
      id: 0,
      insertLoading: false,
      scroll: {
        x: false
      },
      record: {},
      rowSelection: {
        selectedRowKeys: [],
        onChange: this.hanldeRowSelectChange
      },
      isPutModalVisible: false,
      appVersionForm: {
        appVersion: ""
      },
      rules: {},
      isAppVersionUpdating: false
    }
  },
  created() {
    this.queryParams = {
      query: [],
      filter: [],
      sort: [
        {
          field: 'updatedAt',
          value: 'DESC'
        }
      ]
    }
  },
  activated() {
    const a = JSON.parse(sessionStorage.getItem('authorities'))
    if (a?.length) {
      this.authorities = a
    } else {
      this.authorities = this.getAuthorities(this.$route.path)
    }
    document.addEventListener('click', this.handleGlobalClick)
  },
  computed: {
    lang() {
      return this.$store.state.lang
    }
  },
  watch: {
    isOrganizationsShow(val) {
      if (!val && this.organizationNamePlaceholder !== '请选择归属组织') {
        this.organizationName = this.organizationNamePlaceholder
      }
    },
    isRolesShow(val) {
      if (!val && this.roleNamePlaceholder !== '请选择角色') {
        this.roleName = this.roleNamePlaceholder
      }
    },
    lang: {
      immediate: true,
      handler() {
        this.translate()
      }
    }
  },
  mounted() {
    this.getUserList()
    // this.getRoleList()
    // this.getOrganizationList()
    // this.$nextTick(() => {
    //   const ss = document.querySelectorAll('.ant-table-column-title > div span')
    //   ss.forEach((s, i) => {
    //     s.addEventListener('click', () => {
    //       const index = +ss[i].dataset['index']
    //       ss[i].className = ss[i].className === 'ASC icons' ? 'DESC icons' : ss[i].className === 'DESC icons' ? 'unset icons' : 'ASC icons'
    //       if (ss[i].className === 'unset icons') {
    //         const ii = this.query.sort.findIndex(item => item.field === this.columns[index].key)
    //         this.query.sort.splice(ii, 1)
    //       } else {
    //         const item = this.query.sort.find(s => {
    //           const key = this.columns[index].key
    //           return s.field === key
    //         })
    //         if (item) {
    //           item.value = ss[i].className
    //         } else {
    //           this.query.sort.push({
    //             field: this.columns[index].key,
    //             value: ss[i].className
    //           })
    //         }
    //       }
    //       this.searchUserList()
    //     })
    //   })
    // })
      const ss = document.querySelectorAll('.user .ant-table-column-title > div span.icons')
      ss.forEach(i => {
        i.addEventListener('click', () => {
          let c = ''
          switch (i.classList[1]) {
            case 'ASC':
              c = 'DESC'
              break;
            case 'DESC':
              c = 'unset'
              break;
            case 'unset':
              c = 'ASC'
              break;
            default:
              break;
          }
          i.classList.remove(i.classList[1])
          i.classList.add(c)
          const ii = document.querySelectorAll('.user .ant-table-column-title > div span.icons:not(.unset)')
          const sort = Array.from(ii).map(item => {
            const index = +item.dataset['index']
            console.log(index);
            return {
              field: this.columns[index].key,
              value: item.classList[1]
            }
          })
          this.queryParams.sort = sort
          this.searchUserList()
        })
      })
      const ips = document.querySelectorAll('.search .ant-input')
      ips.forEach(ip => {
        ip.addEventListener('keyup', ({ key, target }) => {
          // console.log(key, target.value.trim())
          if (key === 'Enter') {
            target.value = target.value.trim()
            // target.value.trim() && this.handleSearch()
            this.handleSearch()
          }
        })
        ip.addEventListener('blur', ({ target }) => {
          target.value = target.value.trim()
        })
      })
      this.s = document.querySelector('body')
      this.s.addEventListener('click', this.handleSelect)
      const width = window.innerWidth
      if (width < 1600) {
        this.scroll.x = 1350
      } else if (width < 1900) {
        this.scroll.x = 1647
      } else {
        this.scroll.x = false
      }
      document.addEventListener('click', this.handleGlobalClick)
  },
  deactivated() {
    document.removeEventListener('click', this.handleGlobalClick)
  },
  methods: {
    translate() {
      this.rules = {
        appVersion: [
          {
            required: true,
            message: this.$t('user.eav'),
            trigger: "blur"
          }
        ]
      }
    },
    handleSelect(e) {
      const f = document.querySelector('.menu')
      if(f && e.target.classList[1] === 'ant-select-dropdown-menu-item-selected' && !this.visible) {
        this.handleSearch()
      }
    },
    async getUserList() {
      this.searchUserList()
    },
    async getAllRole() {
      this.isRoleLoading = true
      try {
        let res = await searchRoleList(1, 1000, {
          query: [],
          filter: [],
          sort: []
        })
        if (res.data.total > 100) {
          res = await searchRoleList(1, res.data.total, {
            query: [],
            filter: [],
            sort: []
          })
        }
        this.roles = res.data.records
      } catch(e) {
        console.log(e)
      } finally {
        this.isRoleLoading = false
      }
    },
    async getRoleList(e) {
      if (e.key === 'Enter') {
        this.roleName = this.roleName.trim()
        if (this.roleName) {
          try {
            this.rolePage = 1
            this.isRoleLoading = true
            this.roles = []
            const { data } = await searchRoleList(this.rolePage, 20, {
              query: [
                {
                  field: 'details.name',
                  value: this.roleName
                }
              ],
              filter: [],
              sort: []
            })
            this.roles = data.records
          } catch(e) {
            console.log(e)
          } finally {
            this.isRoleLoading = false
          }
        } else {
          this.getAllRole()
        }
      }
    },
    async getAllOrga() {
      this.isOrganizationLoading = true
      try {
        let res = await searchOrganizationList(1, 1000, {
          query: [],
          filter: [],
          sort: []
        })
        if (res.data.total > 1000) {
          res = await searchOrganizationList(1, res.data.total, {
            query: [],
            filter: [],
            sort: []
          })
        }
        this.organizations = res.data.records
      } catch(e) {
        console.log(e)
      } finally {
        this.isOrganizationLoading = false
      }
    },
    async getOrganizationList(e) {
      if (e.key === 'Enter') {
        this.organizationName = this.organizationName.trim()
        if (this.organizationName) {
          try {
            this.organizationsPage = 1
            this.isOrganizationLoading = true
            this.organizations = []
            const { data } = await searchOrganizationList(this.organizationsPage, 20, {
              query: [
                {
                  field: 'details.name',
                  value: this.organizationName
                }
              ],
              filter: [],
              sort: []
            })
            this.organizations = data.records
          } catch(e) {
            console.log(e)
          } finally {
            this.isOrganizationLoading = false
          }
        } else {
          this.getAllOrga()
        }
      }
    },
    selectOrganization(value) {
      this.form.organizationId = value
    },
    selectRole(value) {
      this.form.roleId = value
    },
    handleSearch() {
      this.pagination.current = 1
      const ss = document.querySelectorAll('.user .ant-table .ant-select')
      this.queryParams.filter = []
      this.queryParams.query = []
      ss.forEach(s => {
        const vDom = s.querySelector('.user .ant-table-column-title .ant-select-selection-selected-value')
        if (vDom) {
          const index = +s.dataset['index']
          if (index === 6 || (index !== 6 && vDom.innerText !== '全部' && vDom.innerText !== 'all')) {
            this.queryParams.filter.push({
              field: this.columns[index].key,
              value: booleanMap[vDom.innerText]
            })
          }
        }
      })
      const ips = document.querySelectorAll('.search .ant-input')
      ips.forEach(ip => {
        ip.value = ip.value.trim()
        if (ip.value) {
          this.queryParams.query.push({
            field: this.columns[+ip.dataset['index']].key,
            value: ip.value
          })
          this.highLightInfo[this.columns[+ip.dataset['index']].key] = ip.value
        } else {
          this.highLightInfo[this.columns[+ip.dataset['index']].key] = ''
        }
      })
      this.searchUserList()
    },
    async searchUserList(callback) {
      this.isLoading = true
      try {
        const { data } = await searchUserList(this.pagination.current, this.pagination.pageSize, this.queryParams)
        this.data = data.records.map(item => {
          item.createdAt = moment(item.createdAt).format('YYYY-MM-DD HH:mm:ss')
          item.updatedAt = moment(item.updatedAt).format('YYYY-MM-DD HH:mm:ss')
          return item
        })
        if (callback) {
          this.search = true
          await callback()
          this.pagination.current = data.current
          this.pagination.total = data.total
        } else {
          this.pagination.current = data.current
          this.pagination.total = data.total
        }
      } catch(e) {
        console.log(e)
      } finally {
        this.isLoading = false
      }
    },
    async handleReset(clearFilters, dataIndex) {
      clearFilters()
      this.searchInfo.text[dataIndex] = ''
      this.searchInfo.column[dataIndex] = false
      if (dataIndex === 'realName') {
        this.query.query[0].value = ''
      }
      if (dataIndex === 'username') {
        this.query.query[1].value = ''
      }
      this.searchUserList()
    },
    async removeUser(record) {
      try {
        const { message } = await removeUser(record.id)
        this.$message.success(message)
        if(this.data.length === 1 && this.data[0].id === record.id) {
          this.pagination.current = this.pagination.current === 1 ? 1 : this.pagination.current - 1
        }
        this.searchUserList()
      } catch(e) {
        console.log(e)
        this.$message.error(this.$t('messages.ftd'))
      }
    },
    submitForm() {
      this.$refs.form.validate(async valid => {
        if (valid) {
          if (!this.authorities.find(a => a.code === 'auth:user:insert' || a.code === 'auth:user:update' || a.code === 'agent:auth:user:insert' || a.code === 'agent:auth:user:update')) {
            if (!this.originOrganizations.find(o => o.id === this.form.organizationId) || !this.originRoles.find(r => r.id === this.form.roleId)) {
              return this.$message.warn('无操作权限')
            }
          }
          try {
            const form = { ...this.form, pid: +this.form.pid }
            if (form.roleId === 1 || form.roleId === 2) {
              form.admin = true
            } else {
              form.admin = false
            }
            if (this.form.id) {
              const { message } = await updateUser(form)
              if (form.username !== this.mobile) {
                await resetPassword(form.id)
              }
              this.$message.success(message)
            } else {
              const { message } = await addUser(form)
              this.$message.success(message)
            }
            this.searchUserList()
            this.visible = false
            this.mobile = ''
            this.clearForm()
          } catch(e) {
            console.log(e)
          }
        }
      })
    },
    handleAfterClose() {
      this.searchUserList()
      this.visible = false
      this.mobile = ''
    },
    async showEditModal(record) {
      this.record = record
      this.id = record.id
      this.mobile = record.username
      this.title = '编辑用户'
      this.visible = true
    },
    beforeOpenModal() {
      this.visible = true
    },
    clearForm() {
      this.form = {
        username: '',
        realName: '',
        admin: false,
        organizationId: 204,
        roleId: 4,
        enabled: true,
        requiredCaptcha: true,
        pid: '',
        loginPlatform: 0
      }
      this.organizationName = ''
      this.organizationNamePlaceholder = '请选择归属组织'
      this.organizations = []
      this.roleName = ''
      this.roleNamePlaceholder = '请选择角色'
      this.roles = []
      // this.organizations = []
      // this.roles = []
      this.id = 0
      this.record = {}
    },
    handleTableChange(pagination) {
      this.pagination = pagination
      this.searchUserList()
    },
    async resetP() {
      try {
        const { message } = await resetPassword(this.form.id)
        this.$message.success(message)
      } catch(e) {
        console.log(e)
      }
    },
    handleSelectOrganization(i) {
      this.form.organizationId = i.id
      this.organizationNamePlaceholder = this.organizationName = i.details.find(d => d.locale === 'zh-CN').name
      this.isOrganizationsShow = false
    },
    handleSelectRole(i) {
      this.form.roleId = i.id
      this.roleNamePlaceholder = this.roleName = i.details.find(d => d.locale === 'zh-CN').name
      this.isRolesShow = false
    },
    handleGlobalClick(e) {
      if (!['UL', 'LI', 'INPUT'].includes(e.target.nodeName)) {
        this.isOrganizationsShow = false
        this.isRolesShow = false
      }
    },
    handleOFocus() {
      this.organizationNamePlaceholder = this.organizationName ? this.organizationName : '请选择归属组织'
      this.organizationName = ''
      this.isOrganizationsShow = true
      this.isRolesShow = false
    },
    handleRFocus() {
      this.roleNamePlaceholder = this.roleName ? this.roleName : '请选择角色'
      this.roleName = ''
      this.isRolesShow = true
      this.isOrganizationsShow = false
    },
    flattenArray(arr, origin) {
      arr.forEach(i => {
        origin.push(i)
        if (i.children?.length) {
          this.flattenArray(i.children, origin)
        }
      })
    },
    closeModal() {
      this.visible = false
      this.id = 0
      this.record = {}
    },
    hanldeRowSelectChange(selectedRowKeys) {
      this.rowSelection.selectedRowKeys = selectedRowKeys
    },
    showBatchPutModal() {
      if (!this.rowSelection.selectedRowKeys.length) {
        return this.$message.warning(this.$t("order_operators.su"))
      }
      this.isPutModalVisible = true
    },
    clearAppVersionForm() {
      this.appVersionForm = {
        appVersion: ""
      }
    },
    async updateAppVersions() {
      this.isAppVersionUpdating = true
      try {
        const data = this.rowSelection.selectedRowKeys.map(s => ({
          id: s,
          appVersion: this.appVersionForm.appVersion
        }))
        const { message } = await updateAppVersions(data)
        this.$message.success(message)
        this.isPutModalVisible = false
        this.getUserList()
      } catch(e) {
        console.log(e)
      } finally {
        this.isAppVersionUpdating = false
      }
    },
    activeAnalyse() {
      // 跳转到分析页面
      this.$router.push('/analysis')
    }
  }
}
</script>

<style lang="less" scoped>
/deep/ .ant-form-item {
  margin-bottom: 0;
}
.menu {
  margin: 20px;
  header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}
.row {
  margin-top: 5px;
}
.highlight {
  background-color: rgb(255, 192, 105);
  padding: 0px;
}
.ant-table-column-title div {
  position: relative;
}
.ant-table-column-title span {
  position: relative;
}
.ant-table-column-title .anticon-caret-up {
  position: absolute;
  top: -1px;
  right: -18px;
  font-size: 14px;
  color: #ccc;
}
.ant-table-column-title span.ASC .anticon-caret-up {
  color: #1890ff;
}
.ant-table-column-title span.icons {
  position: absolute;
  right: 18px;
}
.ant-table-column-title span.DESC .anticon-caret-down {
  color: #1890ff;
}
.ant-table-column-title .anticon-caret-down {
  position: absolute;
  top: 8px;
  right: -18px;
  font-size: 14px;
  color: #ccc;
}
.organizationId:focus {
  cursor: text !important;
}
.companyList {
    position: absolute;
    background-color: #fff;
    top: calc(100% + 12px);
    right: 0;
    left: 0;
    z-index: 100;
    border-radius: 5px;
    box-shadow: 0 0 5px rgba(0, 0, 0, .3);
    padding: 3px 0;
    min-height: 30px;
    max-height: 420px;
    overflow: auto;
    .spin {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
    li {
      height: 30px;
      line-height: 30px;
      padding: 0 10px;
      cursor: pointer;
      font-size: 14px;
      color: #777;
      &.selected {
        background-color: #e6f7ff;
      }
    }
  }
</style>
